/* <rr-talk> — Cesar a cámara, donde el sonido ES el contenido (reglas, Raw,
   ficha). No es <rr-video>, que es ambiente mudo en bucle.
   - Perezoso: el <video> se crea y recibe src al entrar en pantalla.
   - Fuente: el original 1080×1920 (snippets/talk.liquid); si da error, la copia
     de Shopify. Con ahorro de datos o red lenta, la copia de 480p.
   - Arranca mudo (ningún navegador deja sonar sin un toque). Los subtítulos no van
     quemados: salen en el idioma de la página de la lista oculta que pinta
     snippets/talk.liquid. Un toque da sonido y lo vuelve a empezar; otro lo quita.
   - Suena uno a la vez en toda la página (evento rr:talk-sound).
   - Con prefers-reduced-motion o ahorro de datos no hay autoplay: póster, y el
     toque reproduce o pausa, ya con sonido.
   <rr-reel> (página de reglas): feed vertical con scroll-snap, una regla por
   pantalla. El sonido se queda puesto al pasar a la siguiente, como en
   Instagram, y con sonido, al acabar una regla pasa sola a la siguiente con
   vídeo (mudo, bucle). iOS solo deja sonar sin toque a un <video> que ya sonó por un
   toque, así que el primer toque "bendice" todos los vídeos del reel: play()
   sin src, que no descarga nada. */
(() => {
  if (customElements.get('rr-talk')) return;

  const net = navigator.connection || {};
  const lite = net.saveData === true || /2g|3g/.test(net.effectiveType || '');
  const still = lite || matchMedia('(prefers-reduced-motion: reduce)').matches;
  let reelSound = false;

  class RRTalk extends HTMLElement {
    connectedCallback() {
      if (this.io) return;
      this.reel = this.closest('rr-reel');
      if (!this.hit) {
        this.hit = this.querySelector('.rr-talk__hit');
        this.hit?.addEventListener('click', () => this.toggle());
        this.onSound = (e) => e.detail !== this && this.mute();
        this.onHidden = () => {
          if (document.hidden) this.pause();
          else if (this.seen && !still) this.play();
        };
      }
      document.addEventListener('rr:talk-sound', this.onSound);
      document.addEventListener('visibilitychange', this.onHidden);
      if (still) this.dataset.state = 'still';
      this.io = new IntersectionObserver(([e]) => this.view(e.isIntersecting), { threshold: 0.6 });
      this.io.observe(this);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.io = null;
      document.removeEventListener('rr:talk-sound', this.onSound);
      document.removeEventListener('visibilitychange', this.onHidden);
      this.v?.pause();
    }

    view(on) {
      if (on === !!this.seen) return;
      this.seen = on;
      if (!on) return this.pause();
      // En el reel cada regla vuelve a empezar al volver a ella, como un reel.
      if (this.reel && this.v?.getAttribute('src')) this.v.currentTime = 0;
      if (!still) this.play();
    }

    get video() {
      if (this.v) return this.v;
      const v = document.createElement('video');
      v.muted = true;
      v.playsInline = true;
      v.loop = true;
      v.preload = 'none';
      v.disablePictureInPicture = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      v.setAttribute('aria-hidden', 'true');
      v.tabIndex = -1;
      v.className = 'rr-talk__video';
      v.addEventListener('playing', () => {
        this.classList.add('is-on');
        if (!this.started) {
          this.started = true;
          this.rr('start', { muted: v.muted ? 1 : 0 });
        }
      });
      v.addEventListener('timeupdate', () => this.progress(v));
      ['play', 'pause', 'volumechange'].forEach((t) => v.addEventListener(t, () => this.sync()));
      v.addEventListener('error', () => this.fallback());
      v.addEventListener('ended', () => this.ended());
      this.insertBefore(v, this.hit);
      this.v = v;
      this.subs(v);
      return v;
    }

    // En cada fotograma mientras suena: el subtítulo que toca y, en el reel con
    // sonido, la barra de progreso (avisa de que al acabar pasa a la siguiente).
    subs(v) {
      const cues = [...this.querySelectorAll('.rr-talk__subs li')].map((li) => [+li.dataset.a, +li.dataset.b, li.textContent]);
      let box = null;
      if (cues.length) {
        box = document.createElement('p');
        box.className = 'rr-talk__sub';
        box.setAttribute('aria-hidden', 'true');
        box.hidden = true;
        this.insertBefore(box, this.hit);
      }
      const bar = this.reel && !still ? document.createElement('span') : null;
      if (bar) {
        bar.className = 'rr-talk__bar';
        bar.setAttribute('aria-hidden', 'true');
        this.insertBefore(bar, this.hit);
      }
      if (!box && !bar) return;
      let raf = 0;
      let shown = '';
      const tick = () => {
        const t = v.currentTime;
        if (box) {
          const text = cues.find(([a, b]) => t >= a && t < b)?.[2] || '';
          if (text !== shown) {
            box.textContent = shown = text;
            box.hidden = !text;
          }
        }
        if (bar && v.duration) bar.style.transform = `scaleX(${t / v.duration})`;
        raf = v.paused ? 0 : requestAnimationFrame(tick);
      };
      v.addEventListener('playing', () => (raf ||= requestAnimationFrame(tick)));
      v.addEventListener('seeked', () => raf || tick());
    }

    // Medición (rr_video, docs/plan-de-medicion.md §4.11): arranca, se le da o quita el sonido y, con
    // sonido, hasta dónde se escucha (25/50/75/100 %, una vez por vídeo y página). video_name es el
    // de sus subtítulos (r01…r12, raw) y video_location la sección.
    rr(action, extra) {
      if (!window.rrTrack) return;
      const s = this.closest('.shopify-section');
      window.rrTrack('rr_video', Object.assign({ action, video_name: this.dataset.rrName, video_location: s ? s.id.split('__').pop() : undefined }, extra));
    }

    progress(v) {
      if (v.muted || !v.duration) return;
      const pct = (v.currentTime / v.duration) * 100;
      this.marks = this.marks || [25, 50, 75, 100];
      // el último timeupdate llega un poco antes del final
      while (this.marks.length && pct >= Math.min(this.marks[0], 98)) this.rr('progress', { percent: this.marks.shift() });
    }

    // Reel con sonido (Cesar, 2 oct 2026): al acabar, a la siguiente regla con vídeo.
    // Mudo sigue en bucle; si el reel no pasa (última, o el visitante ya se movió
    // por su cuenta), vuelve a empezar como antes.
    ended() {
      const v = this.v;
      if (this.reel?.next(this)) return;
      v.currentTime = 0;
      v.play().catch(() => {});
    }

    load(preload) {
      const v = this.video;
      if (!v.getAttribute('src')) {
        const d = this.dataset;
        v.src = this.fell ? d.fallback : (lite && d.lite) || d.src || d.fallback;
      }
      if (preload && v.preload !== 'auto') v.preload = preload;
      return v;
    }

    // El original no cargó o no se decodifica: la copia de Shopify, una vez.
    fallback() {
      const v = this.v;
      const fb = this.dataset.fallback;
      if (!v?.getAttribute('src') || this.fell || !fb || v.getAttribute('src') === fb) return;
      this.fell = true;
      v.src = fb;
      if (this.seen && (!still || this.loud)) this.play();
    }

    unload() {
      const v = this.v;
      if (!v?.getAttribute('src')) return;
      v.pause();
      v.removeAttribute('src');
      v.load();
      this.classList.remove('is-on');
    }

    play() {
      const v = this.load();
      v.muted = !(this.reel ? reelSound : this.loud);
      if (!v.muted) this.claim();
      v.play().catch((err) => {
        if (err?.name === 'AbortError') return;
        if (!v.muted) {
          // Sin permiso para sonar (un vídeo que iOS no tenía bendecido): mudo.
          v.muted = true;
          this.loud = false;
          if (this.reel) reelSound = false;
          v.play().catch(() => this.stall());
        } else this.stall();
      });
    }

    // Ni mudo deja arrancar (iOS en modo de bajo consumo): póster y "play".
    stall() {
      this.stalled = true;
      this.sync();
    }

    pause() {
      this.v?.pause();
    }

    mute() {
      if (!this.v || this.v.muted) return;
      this.v.muted = true;
      this.loud = false;
    }

    claim() {
      document.dispatchEvent(new CustomEvent('rr:talk-sound', { detail: this }));
    }

    toggle() {
      const v = this.load();
      const on = still || this.stalled ? v.paused : v.muted || v.paused;
      if (!on) {
        if (still || this.stalled) v.pause();
        else v.muted = true;
        this.loud = false;
        if (this.reel) reelSound = false;
        this.rr('sound_off');
        return;
      }
      v.muted = false;
      this.loud = true;
      this.rr('sound_on');
      if (this.reel) {
        reelSound = true;
        this.reel.bless(this);
      }
      this.claim();
      // El play() va dentro del toque: es el que da permiso para sonar en iOS.
      v.play().catch(() => {});
      // La primera vez, desde el principio. Rebobinar un vídeo en marcha (y a la
      // vez quitarle el silencio) puede dejar la voz desacompasada de los labios
      // hasta el siguiente salto (Cesar, 1 oct 2026, regla 05): parado, salto, y
      // a andar cuando el salto ha terminado.
      if (!this.heard) {
        this.heard = true;
        // Sin datos o ya al principio no hay salto (ni "seeked" que esperar).
        if (v.readyState < 1 || v.currentTime < 0.1) return;
        v.pause();
        v.addEventListener('seeked', () => !v.muted && v.play().catch(() => {}), { once: true });
        v.currentTime = 0;
      }
    }

    // Dentro del toque: iOS dejará sonar a este vídeo más tarde sin otro toque.
    bless() {
      const v = this.video;
      if (this.blessed || !v.paused) return;
      this.blessed = true;
      const muted = v.muted;
      v.muted = false;
      v.play().catch(() => {});
      v.pause();
      v.muted = muted;
    }

    sync() {
      const v = this.v;
      // En el reel, con sonido no hay bucle: el "ended" es el que pasa a la siguiente.
      if (v && this.reel) v.loop = v.muted || still;
      const playing = v && !v.paused;
      this.dataset.state = playing ? (v.muted ? 'muted' : 'loud') : still || this.stalled ? 'still' : 'muted';
      this.hit?.setAttribute('aria-pressed', String(!!playing && !v.muted));
    }
  }

  class RRReel extends HTMLElement {
    connectedCallback() {
      if (this.feed) return;
      this.feed = this.querySelector('[data-rr-feed]');
      this.slides = [...this.querySelectorAll('[data-rr-slide]')];
      if (!this.feed || !this.slides.length) return;

      this.fit();
      const hit = location.hash && this.slides.find((s) => `#${s.id}` === decodeURIComponent(location.hash));
      if (hit) this.jump(hit.offsetTop);

      let raf = 0;
      addEventListener('resize', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.fit(true));
      });
      // Con las fuentes ya puestas la cabecera puede medir unos píxeles más.
      if (document.readyState !== 'complete') addEventListener('load', () => this.fit(true), { once: true });
      this.querySelectorAll('[data-rr-step]').forEach((b) => b.addEventListener('click', () => this.step(Number(b.dataset.rrStep))));
      this.querySelectorAll('[data-rr-share]').forEach((b) => b.addEventListener('click', () => this.share(b)));
      document.addEventListener('keydown', (e) => this.key(e));
      this.feed.addEventListener('scroll', () => this.moved(), { passive: true });
      // Rueda o dedo sobre el feed: el visitante manda, esta regla ya no pasa sola.
      ['wheel', 'touchmove'].forEach((t) => this.feed.addEventListener(t, () => (this.manualAt = performance.now()), { passive: true }));

      const io = new IntersectionObserver(
        (entries) => entries.forEach((e) => e.isIntersecting && this.activate(e.target)),
        { root: this.feed, threshold: 0.6 }
      );
      this.slides.forEach((s) => io.observe(s));

      // Pista de que hay más debajo: el feed asoma la siguiente regla y vuelve.
      if (!still) setTimeout(() => !this.classList.contains('is-moved') && this.classList.add('is-hint'), 2800);
    }

    // El reel mide lo que queda de pantalla debajo de la cabecera (y de la barra
    // de anuncios si está arriba del todo), así no se corta por abajo al cargar.
    fit(realign) {
      const top = this.getBoundingClientRect().top + scrollY;
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--rr-header-h')) || 64;
      const offset = top < innerHeight * 0.4 ? top : header;
      this.style.setProperty('--rr-reel-top', `${Math.round(offset)}px`);
      if (realign && this.current) this.jump(this.current.offsetTop);
    }

    // Colocar el feed sin la animación suave del CSS (que es para los saltos de
    // la esfera): al cargar en una regla enlazada o al cambiar de tamaño.
    jump(y) {
      const f = this.feed;
      f.style.scrollBehavior = 'auto';
      f.scrollTop = y;
      f.style.scrollBehavior = '';
    }

    moved() {
      if (this.classList.contains('is-moved')) return;
      this.classList.add('is-moved');
      this.classList.remove('is-hint');
    }

    activate(slide) {
      if (this.current === slide) return;
      const first = !this.current;
      this.current = slide;
      this.activeAt = performance.now();
      const i = this.slides.indexOf(slide);
      // Medición (rr_rule_open): la regla que se pone en pantalla, también la primera
      if (window.rrTrack) window.rrTrack('rr_rule_open', { rule_number: slide.dataset.rule, position: i + 1, first: first ? 1 : undefined });
      this.classList.toggle('is-first', i === 0);
      this.classList.toggle('is-last', i === this.slides.length - 1);
      if (!first) {
        this.moved();
        if (slide.id && !window.Shopify?.designMode) history.replaceState(history.state, '', `#${slide.id}`);
      }
      // Solo viven la regla en pantalla y sus vecinas; la siguiente se va cargando.
      this.slides.forEach((s, j) => Math.abs(j - i) > 1 && s.querySelector('rr-talk')?.unload());
      if (!lite) this.slides[i + 1]?.querySelector('rr-talk')?.load(this.classList.contains('is-moved') ? 'auto' : 'metadata');
    }

    // La regla en pantalla acabó con sonido: a la siguiente que tenga vídeo, salvo que
    // el visitante se haya movido él mismo mientras sonaba, o pida menos movimiento.
    next(talk) {
      const i = this.slides.indexOf(this.current);
      // El gesto que trajo esta regla no cuenta: solo lo que se mueva después.
      const manual = this.manualAt > this.activeAt + 1500;
      if (still || document.hidden || manual || talk.closest('[data-rr-slide]') !== this.current) return false;
      const to = this.slides[i + 1];
      if (!to?.querySelector('rr-talk')) return false;
      this.feed.scrollTo({ top: to.offsetTop, behavior: 'smooth' });
      return true;
    }

    step(d) {
      const i = Math.max(0, Math.min(this.slides.length - 1, this.slides.indexOf(this.current) + d));
      this.feed.scrollTo({ top: this.slides[i].offsetTop, behavior: still ? 'auto' : 'smooth' });
    }

    key(e) {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.target.closest?.('input, textarea, select, [contenteditable], details[open], dialog')) return;
      const r = this.getBoundingClientRect();
      if (r.top > innerHeight / 2 || r.bottom < innerHeight / 2) return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        this.step(1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        this.step(-1);
      } else if (e.key === 'm' || e.key === 'M') {
        this.current?.querySelector('rr-talk')?.toggle();
      }
    }

    bless(except) {
      this.querySelectorAll('rr-talk').forEach((t) => t !== except && t.bless());
    }

    // "Send it" (regla 12): comparte el enlace a esta regla.
    async share(btn) {
      const slide = btn.closest('[data-rr-slide]');
      const url = `${location.origin}${location.pathname}${slide?.id ? `#${slide.id}` : ''}`;
      const title = slide?.querySelector('h2')?.textContent.trim() || document.title;
      if (window.rrTrack) window.rrTrack('rr_share', { method: navigator.share ? 'native' : 'copy', rule_number: slide?.dataset.rule });
      try {
        if (navigator.share) return await navigator.share({ title, url });
        await navigator.clipboard.writeText(url);
        const label = btn.querySelector('[data-rr-share-label]');
        if (!label || !btn.dataset.done) return;
        const text = label.textContent;
        label.textContent = btn.dataset.done;
        setTimeout(() => (label.textContent = text), 2200);
      } catch {
        // compartir cancelado o sin permiso de portapapeles: nada que hacer
      }
    }
  }

  customElements.define('rr-talk', RRTalk);
  customElements.define('rr-reel', RRReel);
})();
